import { clsx, type ClassValue } from 'clsx';
import { format, parseISO, isAfter, isBefore, differenceInDays } from 'date-fns';
import type { StageStatus, InvoiceStatus, ProjectStatus } from '../types';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/** Admin-level roles: super_admin carries every admin permission. */
export function isAdminRole(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'super_admin';
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try {
    return format(parseISO(dateStr), 'd MMM yyyy');
  } catch {
    return dateStr;
  }
}

export function formatDateTime(dateStr: string): string {
  if (!dateStr) return '—';
  try {
    return format(parseISO(dateStr), 'd MMM yyyy, HH:mm');
  } catch {
    return dateStr;
  }
}

export function formatCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export function isOverdue(dateStr: string): boolean {
  if (!dateStr) return false;
  try {
    return isBefore(parseISO(dateStr), new Date());
  } catch {
    return false;
  }
}

/**
 * Updates for manually marking an invoice overdue. The server's auto-rule
 * keeps "overdue" only while the payment deadline is in the past — so if the
 * invoice has no past due date, we set the due date to yesterday. That makes
 * the data consistent with the status and the mark sticks.
 */
export function overdueStatusUpdates(inv: { dueDate?: string | null }): { status: 'overdue'; dueDate?: string } {
  const today = new Date().toISOString().split('T')[0];
  const due = inv.dueDate?.split('T')[0];
  if (due && due < today) return { status: 'overdue' };
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  return { status: 'overdue', dueDate: yesterday };
}

/**
 * The date that places an invoice on the money timeline: when it was paid,
 * else when it actually went out, else when it is planned to go out.
 *
 * An invoice planned for August but sent in October belongs to October — the
 * month the money really started moving. Keying cash-flow views on the planned
 * date alone hid such invoices in a month nobody was looking at any more.
 */
export function invoiceTimelineDate(inv: {
  status: InvoiceStatus;
  paidDate?: string | null;
  sentDate?: string | null;
  plannedSendDate?: string | null;
}): string | null {
  if (inv.status === 'paid' && inv.paidDate) return inv.paidDate;
  if ((inv.status === 'sent' || inv.status === 'overdue') && inv.sentDate) return inv.sentDate;
  return inv.plannedSendDate || null;
}

export function daysUntil(dateStr: string): number {
  if (!dateStr) return 0;
  try {
    return differenceInDays(parseISO(dateStr), new Date());
  } catch {
    return 0;
  }
}

export function isApproaching(dateStr: string, withinDays = 7): boolean {
  if (!dateStr) return false;
  try {
    const d = daysUntil(dateStr);
    return d >= 0 && d <= withinDays;
  } catch {
    return false;
  }
}

export const STAGE_STATUS_CONFIG: Record<StageStatus, { label: string; color: string; bg: string }> = {
  not_started: { label: 'Not Started', color: 'text-gray-600', bg: 'bg-gray-100' },
  in_progress: { label: 'In Progress', color: 'text-blue-700', bg: 'bg-blue-100' },
  blocked: { label: 'Blocked', color: 'text-red-700', bg: 'bg-red-100' },
  completed: { label: 'Completed', color: 'text-green-700', bg: 'bg-green-100' },
};

export const INVOICE_STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; bg: string; dot: string }> = {
  not_ready: { label: 'Not Ready', color: 'text-gray-600', bg: 'bg-gray-100', dot: 'bg-gray-400' },
  ready_to_send: { label: 'Ready to Send', color: 'text-yellow-700', bg: 'bg-yellow-100', dot: 'bg-yellow-500' },
  sent: { label: 'Sent', color: 'text-blue-700', bg: 'bg-blue-100', dot: 'bg-blue-500' },
  paid: { label: 'Paid', color: 'text-green-700', bg: 'bg-green-100', dot: 'bg-green-500' },
  overdue: { label: 'Overdue', color: 'text-red-700', bg: 'bg-red-100', dot: 'bg-red-500' },
};

export const PROJECT_STATUS_CONFIG: Record<ProjectStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: 'text-gray-600', bg: 'bg-gray-100' },
  active: { label: 'Active', color: 'text-blue-700', bg: 'bg-blue-100' },
  on_hold: { label: 'On Hold', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  completed: { label: 'Completed', color: 'text-green-700', bg: 'bg-green-100' },
  cancelled: { label: 'Cancelled', color: 'text-red-700', bg: 'bg-red-100' },
};

export function generateId(): string {
  return Math.random().toString(36).slice(2, 11) + Date.now().toString(36);
}
